import React, { useRef } from "react"
import PropTypes from "prop-types"

import classnames from "classnames"

import { connectionShape } from "app/lib/shared_prop_types"

import Fittty from "shared/components/fittty"
import NeverEnabledMeta from "./never_enabled_meta"
import EnabledMeta from "./enabled_meta"
import AuthorInfo from "shared/components/connection_card/author_info"
import { WorksWithContent } from "./works_with_content"

import styles from "./small_applet_card.module.scss"

// className need to be passed manually
// otherwise the transpiler does not detect the properties
const LinkableAppletWrapper = React.forwardRef(({ children, href, onClick, className, ...attributes }, ref) => {
  if (href || onClick) {
    return (
      <a ref={ref} className={`${className} ${styles["applet-card-body"]}`} onClick={onClick} href={href} {...attributes}>
        {children}
      </a>
    )
  } else {
    return (
      <div ref={ref} className={`${className} ${styles["applet-card-body"]}`} {...attributes}>
        {children}
      </div>
    )
  }
})

export default function SmallAppletCard(props) {
  const {
    applet,
    appletPath,
    availableToRestore,
    extraClass,
    isOnboardingApplet,
    onAppletClick,
    showConnectButton,
    showRestoreButton,
    target,
    useDeactivatedColor,
    position,
    context,
    ...linkProps
  } = props

  const cardRef = useRef(null)

  const mainService =
    applet.channels.find(el => {
      return el.module_name === applet.service_slug
    }) || applet.channels[0]
  const backgroundImage =
    applet.background_images?.background_image_url_1x || applet.background_images?.sm_background_image_url
  const disconnectedState = applet.status === "disabled_for_user" || useDeactivatedColor
  const style = disconnectedState ? { backgroundColor: "#aaa" } : { backgroundColor: `#${mainService.brand_color}` }

  if (backgroundImage) {
    if (disconnectedState) {
      // rgba(170, 170, 170, .58) is #aaa with 42% alpha
      style.backgroundImage = `linear-gradient(to bottom, rgba(170, 170, 170, .58), rgba(170, 170, 170, .58)), url('${backgroundImage}')`
    } else {
      style.backgroundImage = `linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(0, 0, 0, 0.4)), url('${backgroundImage}')`
    }
  }

  return (
    <LinkableAppletWrapper
      ref={cardRef}
      style={style}
      className={classnames({
        "applet-card-body": true,
        [`applet-${applet.id}`]: true,
        [`${extraClass}`]: true,
        "applet-card-no-link": !props.appletPath,
      })}
      href={appletPath}
      onClick={onAppletClick}
      target={target}
      data-applet-id={applet.id}
      data-applet-owner-type={applet.by_service_owner ? "service" : "user"}
      data-applet-published-type={applet.published ? "published" : "diy"}
      data-applet-author={applet.author?.login}
      data-position={position}
      data-context={context}
      {...linkProps}
    >
      <div className="meta-header">
        <WorksWithContent applet={applet} mainService={mainService} />
      </div>
      <div className="content">
        <div
          className={classnames("title", {
            "with-image": backgroundImage,
          }, {
            [styles["with-image"]]: backgroundImage,
          })}
        >
          <Fittty mode="box" text={applet.name} />
        </div>
        <AuthorInfo connection={applet} serviceIcon={applet.monochrome_icon_url} />
      </div>
      {showRestoreButton && (
        <div className={styles["restore-button"]}>
          <button className={classnames("button-tertiary", { [styles.disabled]: !availableToRestore })}>
            {availableToRestore ? "Restore" : "Can't restore"}
          </button>
        </div>
      )}
      {!applet.status || (applet.status === "never_enabled_for_user" && !isOnboardingApplet) ? (
        <NeverEnabledMeta applet={applet} />
      ) : (
        <EnabledMeta
          applet={applet}
          mainService={mainService}
          href={appletPath}
          onAppletClick={onAppletClick}
          appearDisabled={disconnectedState}
          showConnectButton={showConnectButton}
          isOnboardingApplet={isOnboardingApplet}
        />
      )}
    </LinkableAppletWrapper>
  )
}

SmallAppletCard.propTypes = {
  applet: connectionShape.isRequired,
  appletPath: PropTypes.string,
  availableToRestore: PropTypes.bool,
  extraClass: PropTypes.string,
  isOnboardingApplet: PropTypes.bool,
  onAppletClick: PropTypes.func,
  showConnectButton: PropTypes.bool,
  showRestoreButton: PropTypes.bool,
  target: PropTypes.string,
  term: PropTypes.string,
  useDeactivatedColor: PropTypes.bool,
  position: PropTypes.number,
  context: PropTypes.string,
}

SmallAppletCard.defaultProps = {
  availableToRestore: false,
  extraClass: "",
  isOnboardingApplet: false,
  onAppletClick: () => {},
  showConnectButton: true,
  showRestoreButton: false,
  useDeactivatedColor: false,
}
